/**
 * Slot Utility Functions
 *
 * Utility functions for processing timeslots including status derivation,
 * selection validation, and time period grouping.
 *
 * @requirements 1.2 - Time period grouping (Morning, Afternoon, Evening)
 * @requirements 1.4 - Blocked slot handling
 * @requirements 2.5 - Selection prevention for blocked slots
 */

import { TimeSlot, SlotStatus, TIME_PERIODS } from '../types'

/**
 * Raw timeslot data structure before status derivation.
 * This represents the data as it comes from the API/backend.
 */
export interface RawTimeSlot {
  id: string
  time: Date
  duration?: number
  doctorId?: string
  patientId?: string
  appointmentId?: string
  formattedStatus?: {
    color: string
    label: string
  }
}

/**
 * Derives the slot status from raw timeslot data.
 *
 * Status derivation logic:
 * - If patientId is present, the slot is 'booked' (has an appointment)
 * - If doctorId is present (but no patientId), the slot is 'blocked' (time-off)
 * - Otherwise, the slot is 'open' (available for booking)
 *
 * @param slot - Raw timeslot data
 * @returns The derived SlotStatus
 *
 * @requirements 1.4 - Blocked slot identification
 * @requirements 1.5 - Booked slot identification
 */
export function deriveSlotStatus(slot: RawTimeSlot): SlotStatus {
  if (slot.patientId) {
    return 'booked'
  }
  if (slot.doctorId) {
    return 'blocked'
  }
  return 'open'
}

/**
 * Determines if a timeslot can be selected.
 *
 * Only 'open' slots can be selected. Blocked and booked slots
 * cannot be added to the selection set.
 *
 * @param slot - The timeslot to check
 * @returns true if the slot can be selected, false otherwise
 *
 * @requirements 1.4 - Blocked slots cannot be selected
 * @requirements 2.5 - Selection prevention for blocked slots
 */
export function canSelectSlot(slot: TimeSlot): boolean {
  return slot.status === 'open'
}

/**
 * Groups timeslots by time period (Morning, Afternoon, Evening).
 *
 * Time period boundaries:
 * - Morning: 00:00 - 12:00 (hours 0-11)
 * - Afternoon: 12:00 - 18:00 (hours 12-17)
 * - Evening: 18:00 - 24:00 (hours 18-23)
 *
 * @param slots - Array of timeslots to group
 * @returns Map with period IDs as keys and arrays of timeslots as values
 *
 * @requirements 1.2 - Time period grouping
 */
export function groupSlotsByPeriod(slots: TimeSlot[]): Map<string, TimeSlot[]> {
  // Initialize the map with empty arrays for each period
  const grouped = new Map<string, TimeSlot[]>()
  TIME_PERIODS.forEach((period) => grouped.set(period.id, []))

  // Group each slot into the appropriate time period
  slots.forEach((slot) => {
    const hour = slot.time.getHours()
    const period = TIME_PERIODS.find(
      (p) => hour >= p.startHour && hour < p.endHour
    )
    if (period) {
      grouped.get(period.id)!.push(slot)
    }
  })

  return grouped
}

/**
 * Default slot duration in milliseconds (10 minutes)
 */
const DEFAULT_SLOT_DURATION_MS = 10 * 60 * 1000

/**
 * Converts a RawTimeSlot to a TimeSlot with derived status.
 *
 * @param rawSlot - Raw timeslot data from API
 * @returns TimeSlot with derived status
 */
export function processRawSlot(rawSlot: RawTimeSlot): TimeSlot {
  return {
    ...rawSlot,
    duration: rawSlot.duration ?? DEFAULT_SLOT_DURATION_MS,
    status: deriveSlotStatus(rawSlot),
  }
}

/**
 * Processes an array of raw timeslots into TimeSlots with derived statuses.
 *
 * @param rawSlots - Array of raw timeslot data
 * @returns Array of TimeSlots with derived statuses
 */
export function processRawSlots(rawSlots: RawTimeSlot[]): TimeSlot[] {
  return rawSlots.map(processRawSlot)
}
